import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BrandStorySection = () => {
  const [isVisible, setIsVisible] = useState(true); // Changed to true for mobile compatibility
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 } // Lowered threshold for better mobile detection
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const storyMilestones = [
    {
      year: "2019",
      title: "The Royal Vision",
      description: "Founded with a mission to revolutionize online gaming in India by combining traditional values with modern technology.",
      icon: "Lightbulb"
    },
    {
      year: "2020",
      title: "Trust Foundation",
      description: "Established partnerships with leading payment providers and obtained necessary gaming licenses to ensure player security.",
      icon: "Shield"
    },
    {
      year: "2021",
      title: "Community Growth",
      description: "Reached 10,000+ active players through word-of-mouth recommendations and exceptional WhatsApp customer service.",
      icon: "Users"
    },
    {
      year: "2022",
      title: "Innovation Leader",
      description: "Launched India\'s first WhatsApp-integrated gaming platform with personalized advisor support for every player.",
      icon: "Smartphone"
    },
    {
      year: "2023",
      title: "Industry Recognition",
      description: "Awarded \'Best Customer Service\' and \'Most Trusted Platform\' by Indian Gaming Association.",
      icon: "Award"
    },
    {
      year: "2024",
      title: "Royal Legacy",
      description: "Celebrating 50,000+ satisfied players and ₹100Cr+ in secure payouts, establishing Nitin as India's gaming royalty.",
      icon: "Crown"
    }
  ];

  return (
    <section ref={sectionRef} className="py-12 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center shadow-royal">
              <Icon name="BookOpen" size={24} color="white" />
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-gray-900 mb-4">
            Our <span className="text-gradient-royal">Royal Journey</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            From a visionary idea to India's most trusted gaming platform, discover the story behind Nitin's rise to digital royalty.
          </p>
        </div>

        {/* Story Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
          {/* Left - Story Text */}
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="bg-white rounded-2xl p-8 shadow-subtle border border-gray-100">
              <h3 className="text-2xl font-heading font-bold text-gray-900 mb-6">
                The Genesis of Gaming Excellence
              </h3>
              <div className="space-y-4 text-gray-700">
                <p>
                  In the bustling digital landscape of India, Nitin Online Hub was born from a simple yet powerful vision: to create a gaming platform that honors traditional Indian values while embracing cutting-edge technology.
                </p>
                <p>
                  Our founders, seasoned gaming industry veterans, recognized that Indian players deserved more than just another betting site. They envisioned a royal experience where every player receives personalized attention, instant support, and most importantly, complete trust in their gaming journey.
                </p>
                <p>
                  The name 'Nitin' represents our commitment to ethical gaming practices, while 'Dynasty' symbolizes our aspiration to build a lasting legacy of trust and excellence in the Indian gaming community.
                </p>
              </div>
              
              <div className="mt-8 p-6 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border-l-4 border-primary">
                <div className="flex items-start space-x-4">
                  <Icon name="Quote" size={24} color="var(--color-primary)" />
                  <div>
                    <p className="text-gray-800 font-medium italic">
                      "We didn't just want to create another gaming platform. We wanted to build a digital palace where every player feels like royalty."
                    </p>
                    <p className="text-sm text-gray-600 mt-2">- Founder, Nitin Online Hub</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Visual */}
          <div className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative">
              <Image
                src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=400&fit=crop&crop=center"
                alt="Nitin Brand Story Heritage"
                className="w-full h-80 object-cover rounded-2xl shadow-royal"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-2xl"></div>
              
              {/* Overlay Stats */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/90 backdrop-blur-md rounded-xl p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-primary">5+</p>
                      <p className="text-xs text-gray-600">Years</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-accent">50K+</p>
                      <p className="text-xs text-gray-600">Players</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-success">₹100Cr+</p>
                      <p className="text-xs text-gray-600">Payouts</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className={`transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-2xl font-heading font-bold text-center text-gray-900 mb-12">
            Milestones of Our <span className="text-gradient-royal">Royal Legacy</span>
          </h3>
          
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary to-accent rounded-full hidden lg:block"></div>
            
            <div className="space-y-12">
              {storyMilestones?.map((milestone, index) => (
                <div key={milestone?.year} className={`flex flex-col lg:flex-row items-center ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                  {/* Content */}
                  <div className={`lg:w-5/12 ${index % 2 === 0 ? 'lg:text-right lg:pr-8' : 'lg:text-left lg:pl-8'}`}>
                    <div className="bg-white rounded-xl p-6 shadow-subtle border border-gray-100 hover:shadow-royal transition-all duration-300">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center">
                          <Icon name={milestone?.icon} size={20} color="white" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-gray-900">{milestone?.title}</h4>
                          <p className="text-primary font-bold">{milestone?.year}</p>
                        </div>
                      </div>
                      <p className="text-gray-700">{milestone?.description}</p>
                    </div>
                  </div>
                  
                  {/* Timeline Node */}
                  <div className="lg:w-2/12 flex justify-center my-4 lg:my-0">
                    <div className="w-6 h-6 bg-gradient-royal rounded-full border-4 border-white shadow-royal"></div>
                  </div>
                  
                  {/* Spacer */}
                  <div className="lg:w-5/12"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandStorySection;